import { useState, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, apiPatch, type AuthUser } from '../lib/auth';
import { Toast, type ToastVariant } from '../components/Toast';
import { useLang } from '../lib/lang';

interface Props { user: AuthUser }

interface Reservation {
  id: string; item_id: string; member_id: string;
  title_en: string; catalogue_id: string; cover_image_url: string | null;
  membership_number: string; member_type: string;
  reserved_at: string; expires_at: string; queue_position: number;
  status: string; notified_at: string | null;
}

const BN = {
  pageTitle: 'Tempahan Perpustakaan',
  backLink: '← Laman Utama',
  queueLabel: 'dalam giliran',
  readyLabel: 'sedia diambil',
  reserveBtn: '+ Tempah Bahan',
  reserveFormTitle: 'Buat Tempahan Baharu',
  labelMemberId: 'ID Ahli *',
  labelItemId: 'ID Bahan *',
  confirmBtn: 'Sahkan Tempahan',
  confirmingBtn: 'Membuat tempahan…',
  cancelBtn: 'Batal',
  retryBtn: 'Cuba Lagi',
  emptyTitle: 'Tiada Tempahan',
  emptySubtitle: 'Tiada tempahan dijumpai.',
  cancelActionBtn: '✕ Batal',
  memberLabel: 'Ahli',
  reservedLabel: 'Ditempah',
  expiresLabel: 'Luput',
  queuePosLabel: 'Giliran #',
  notifiedLabel: '✉ Dimaklumkan',
  expiringLabel: '⚠ Hampir luput',
  readyBannerText: 'Bahan sedia untuk diambil di kaunter perpustakaan sebelum',
  statusLabels: {
    QUEUED: 'Dalam Giliran',
    READY: 'Sedia Ambil',
    FULFILLED: 'Dipenuhi',
    EXPIRED: 'Tamat Tempoh',
    CANCELLED: 'Dibatalkan',
  },
};

const EN = {
  pageTitle: 'Library Reservations',
  backLink: '← Home',
  queueLabel: 'in queue',
  readyLabel: 'ready for collection',
  reserveBtn: '+ Reserve Item',
  reserveFormTitle: 'New Reservation',
  labelMemberId: 'Member ID *',
  labelItemId: 'Item ID *',
  confirmBtn: 'Confirm Reservation',
  confirmingBtn: 'Reserving…',
  cancelBtn: 'Cancel',
  retryBtn: 'Retry',
  emptyTitle: 'No Reservations',
  emptySubtitle: 'No reservations found.',
  cancelActionBtn: '✕ Cancel',
  memberLabel: 'Member',
  reservedLabel: 'Reserved',
  expiresLabel: 'Expires',
  queuePosLabel: 'Queue #',
  notifiedLabel: '✉ Notified',
  expiringLabel: '⚠ Expiring soon',
  readyBannerText: 'Item ready for collection at the library counter by',
  statusLabels: {
    QUEUED: 'In Queue',
    READY: 'Ready for Collection',
    FULFILLED: 'Fulfilled',
    EXPIRED: 'Expired',
    CANCELLED: 'Cancelled',
  },
};

const STATUS_ICONS: Record<string, string> = {
  QUEUED: '⏳', READY: '✅', FULFILLED: '✓', EXPIRED: '⌛', CANCELLED: '✕',
};
const STATUS_COLORS: Record<string, string> = {
  QUEUED: '#60a5fa', READY: '#4ade80', FULFILLED: '#9ca3af', EXPIRED: '#f87171', CANCELLED: '#9ca3af',
};

const DEMO_SCHOOL = '3f868b12-b554-4f51-a893-69e9b4ba9f57';
function getSchoolId(user: AuthUser): string { return user.roles.find((r) => r.schoolId)?.schoolId ?? DEMO_SCHOOL; }

export function LibraryReservationsPage({ user }: Props) {
  const { lang } = useLang();
  const UI = lang === 'en' ? EN : BN;

  const schoolId = getSchoolId(user);

  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [toast,        setToast]        = useState<{ message: string; variant: ToastVariant } | null>(null);

  const [filterStatus, setFilterStatus] = useState('QUEUED');
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Reserve form
  const [showForm,  setShowForm]  = useState(false);
  const [resForm,   setResForm]   = useState({ memberId: '', itemId: '' });
  const [resError,  setResError]  = useState<string | null>(null);
  const [reserving, setReserving] = useState(false);

  const STATUS_CFG = Object.fromEntries(
    Object.entries(UI.statusLabels).map(([k, label]) => [k, { label, color: STATUS_COLORS[k] ?? '#9ca3af', icon: STATUS_ICONS[k] ?? '?' }])
  );

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams({ schoolId });
      if (filterStatus) params.set('status', filterStatus);
      setReservations(await apiGet<Reservation[]>(`/api/library/reservations?${params}`));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Ralat memuatkan tempahan.');
    } finally { setLoading(false); }
  }, [schoolId, filterStatus]);

  useEffect(() => { void load(); }, [load]);

  async function handleReserve() {
    if (!resForm.memberId.trim()) { setResError('ID ahli diperlukan. / Member ID required.'); return; }
    if (!resForm.itemId.trim())   { setResError('ID bahan diperlukan. / Item ID required.'); return; }
    setReserving(true); setResError(null);
    try {
      const result = await apiPost<{ id: string; queuePosition: number }>('/api/library/reservations', {
        memberId: resForm.memberId.trim(), itemId: resForm.itemId.trim(),
      });
      setToast({ variant: 'success', message: `Tempahan berjaya. Kedudukan giliran: ${result.queuePosition}. / Reservation placed. Queue position: ${result.queuePosition}.` });
      setShowForm(false); setResForm({ memberId: '', itemId: '' });
      void load();
    } catch (e) {
      setResError(e instanceof Error ? e.message : 'Gagal membuat tempahan. / Failed to reserve.');
    } finally { setReserving(false); }
  }

  async function handleCancel(id: string) {
    setCancellingId(id);
    try {
      await apiPatch(`/api/library/reservations/${id}/cancel`, {});
      setToast({ variant: 'success', message: 'Tempahan berjaya dibatalkan. / Reservation cancelled.' });
      void load();
    } catch (e) {
      setToast({ variant: 'error', message: e instanceof Error ? e.message : 'Gagal membatalkan tempahan.' });
    } finally { setCancellingId(null); }
  }

  // Stats
  const queuedCount = reservations.filter((r) => r.status === 'QUEUED').length;
  const readyCount  = reservations.filter((r) => r.status === 'READY').length;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>

          <div style={{ marginBottom: spacing.xl }}>
            <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>{UI.backLink}</Link>
          </div>

          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: spacing.xl, gap: spacing.md, flexWrap: 'wrap' }}>
            <div>
              <h1 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                🔖 {UI.pageTitle}
              </h1>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {queuedCount} {UI.queueLabel}{readyCount > 0 && <> · <span style={{ color: '#4ade80' }}>{readyCount} {UI.readyLabel}</span></>}
              </p>
            </div>
            <div style={{ display: 'flex', gap: spacing.sm }}>
              <Button variant="ghost" size="sm" onClick={() => void load()} disabled={loading}>
                {loading ? <Spinner size="sm" /> : '↻'}
              </Button>
              {!showForm && (
                <Button variant="primary" size="sm" onClick={() => { setShowForm(true); setResError(null); }}>
                  {UI.reserveBtn}
                </Button>
              )}
            </div>
          </div>

          {/* Reserve form */}
          {showForm && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, marginBottom: spacing.lg }}>
              <p style={{ margin: `0 0 ${spacing.md}`, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {UI.reserveFormTitle}
              </p>
              <div style={{ display: 'flex', gap: spacing.sm, flexWrap: 'wrap', marginBottom: spacing.sm }}>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={labelSt}>{UI.labelMemberId}</label>
                  <input type="text" value={resForm.memberId}
                    onChange={(e) => setResForm((f) => ({ ...f, memberId: e.target.value }))}
                    placeholder="UUID ahli" disabled={reserving} style={inputSt()} />
                </div>
                <div style={{ flex: '1 1 180px' }}>
                  <label style={labelSt}>{UI.labelItemId}</label>
                  <input type="text" value={resForm.itemId}
                    onChange={(e) => setResForm((f) => ({ ...f, itemId: e.target.value }))}
                    placeholder="UUID bahan" disabled={reserving} style={inputSt()} />
                </div>
              </div>
              {resError && <p style={{ margin: `0 0 ${spacing.sm}`, color: '#f87171', fontSize: typography.fontSize.sm }}>{resError}</p>}
              <div style={{ display: 'flex', gap: spacing.sm }}>
                <Button variant="primary" size="sm" onClick={() => void handleReserve()} disabled={reserving} loading={reserving}>
                  {reserving ? UI.confirmingBtn : UI.confirmBtn}
                </Button>
                <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setResError(null); }} disabled={reserving}>{UI.cancelBtn}</Button>
              </div>
            </div>
          )}

          {/* Status filter tabs */}
          <div style={{ display: 'flex', gap: 0, marginBottom: spacing.lg, backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden' }}>
            {Object.entries(STATUS_CFG).map(([v, c]) => (
              <button key={v} onClick={() => setFilterStatus(filterStatus === v ? '' : v)}
                style={{
                  flex: 1, padding: `${spacing.sm} ${spacing.xs}`, background: 'none',
                  borderBottom: filterStatus === v ? `2px solid ${c.color}` : '2px solid transparent',
                  borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                  color: filterStatus === v ? c.color : colors.neutral.gray400,
                  fontSize: typography.fontSize.xs, cursor: 'pointer',
                  fontFamily: typography.fontFamily.body, fontWeight: filterStatus === v ? typography.fontWeight.semibold : typography.fontWeight.medium,
                  whiteSpace: 'nowrap',
                }}>
                {c.icon} {c.label}
              </button>
            ))}
          </div>

          {error && <div style={{ backgroundColor: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.lg }}>
            <p style={{ margin: 0, color: '#f87171', fontSize: typography.fontSize.sm }}>{error}</p>
            <Button variant="ghost" size="sm" onClick={() => void load()} style={{ marginTop: spacing.xs }}>{UI.retryBtn}</Button>
          </div>}

          {loading && reservations.length === 0 && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.xl }}><Spinner size="lg" /></div>}

          {!loading && !error && reservations.length === 0 && (
            <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, padding: spacing.xl, textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>🔖</div>
              <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                {filterStatus ? `${STATUS_CFG[filterStatus]?.label ?? filterStatus} — ${UI.emptyTitle}` : UI.emptyTitle}
              </p>
              <p style={{ margin: `${spacing.xs} 0 0`, color: colors.neutral.gray400, fontSize: typography.fontSize.sm }}>
                {UI.emptySubtitle}
              </p>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: spacing.sm }}>
            {reservations.map((res) => {
              const sc          = STATUS_CFG[res.status] ?? { label: res.status, color: '#9ca3af', icon: '?' };
              const isExpiring  = res.status === 'READY' && new Date(res.expires_at) < new Date(Date.now() + 86_400_000);
              const isCancelling = cancellingId === res.id;

              return (
                <div key={res.id} style={{
                  backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl,
                  border: `1px solid ${res.status === 'READY' ? 'rgba(74,222,128,0.3)' : isExpiring ? 'rgba(248,113,113,0.3)' : colors.neutral.gray800}`,
                  overflow: 'hidden', boxShadow: shadows.lg,
                }}>
                  <div style={{ padding: spacing.lg, display: 'flex', gap: spacing.md, alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: spacing.xs, marginBottom: spacing.xs, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ padding: '2px 10px', backgroundColor: `${sc.color}22`, color: sc.color, borderRadius: borderRadius.full, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold }}>
                          {sc.icon} {sc.label}
                        </span>
                        {res.status === 'QUEUED' && (
                          <span style={{ fontSize: typography.fontSize.xs, color: '#60a5fa', backgroundColor: 'rgba(96,165,250,0.1)', padding: '1px 8px', borderRadius: borderRadius.full }}>
                            {UI.queuePosLabel}{res.queue_position}
                          </span>
                        )}
                        {res.status === 'READY' && res.notified_at && (
                          <span style={{ fontSize: typography.fontSize.xs, color: '#4ade80' }}>
                            {UI.notifiedLabel} {new Date(res.notified_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                          </span>
                        )}
                        {isExpiring && <span style={{ fontSize: typography.fontSize.xs, color: '#f87171', fontWeight: typography.fontWeight.bold }}>{UI.expiringLabel}</span>}
                      </div>

                      <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {res.title_en}
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {res.catalogue_id} · {UI.memberLabel}: {res.membership_number} ({res.member_type})
                      </p>
                      <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                        {UI.reservedLabel}: {new Date(res.reserved_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}
                        {' · '}{UI.expiresLabel}: <span style={{ color: isExpiring ? '#f87171' : colors.neutral.gray400 }}>{new Date(res.expires_at).toLocaleDateString(lang === 'en' ? 'en-GB' : 'ms-BN')}</span>
                      </p>
                    </div>

                    {/* Actions */}
                    {['QUEUED','READY'].includes(res.status) && (
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => void handleCancel(res.id)}
                        disabled={!!cancellingId}
                        loading={isCancelling}
                      >
                        {isCancelling ? '…' : UI.cancelActionBtn}
                      </Button>
                    )}
                  </div>

                  {/* Ready banner */}
                  {res.status === 'READY' && (
                    <div style={{ padding: `${spacing.xs} ${spacing.lg}`, backgroundColor: 'rgba(74,222,128,0.06)', borderTop: `1px solid rgba(74,222,128,0.15)` }}>
                      <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: '#4ade80' }}>
                        ✅ {UI.readyBannerText} {new Date(res.expires_at).toLocaleDateString('ms-BN', { day: 'numeric', month: 'short' })}.
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

const labelSt: React.CSSProperties = { display: 'block', fontSize: typography.fontSize.xs, color: colors.neutral.gray400, marginBottom: spacing.xs };
function inputSt(): React.CSSProperties {
  return {
    width: '100%', backgroundColor: colors.neutral.black,
    border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.md,
    color: colors.neutral.white, padding: `${spacing.xs} ${spacing.sm}`,
    fontSize: typography.fontSize.sm, outline: 'none',
    boxSizing: 'border-box', fontFamily: typography.fontFamily.body,
  };
}
